import { ElementType, useEffect, useRef } from 'react'
import { Grid, Hidden, TextField } from '@mui/material'
import { ISettingsItem } from '../../../../Settings'
import MaskedTextField, { MaskedTextFieldProps } from '../../../MaskedTextField'
import { useForm } from '../../../../Form'
import { useTranslation } from '../../../../i18n'
import { ChoosePlanText } from '../../..'

interface fieldProps extends Omit<MaskedTextFieldProps, "mask"> {
  Component: ElementType
  mask?: string | string[]
}

export default function ContactInfo({ name, variables }: ISettingsItem) {
  const { currentFields, currentFields: { fieldErrors }, formState: { showError }, setCurrentFields, setFormState } = useForm()
  const { t } = useTranslation()
  const fieldsUnfilled = useRef([] as string[])
  const defaultTextArray = [
    t.page2.contactInfo.title,
    t.page2.contactInfo.subtitle,
    t.page2.contactInfo.email,
    t.page2.contactInfo.ddd,
    t.page2.contactInfo.phone,
  ]
  const { text = defaultTextArray } = variables || {}

  const fields: fieldProps[] = [
    {
      Component: TextField,
      required: true,
      helperText: t.common.requiredField,
      name: "email",
      id: "outlined-email-input",
      value: currentFields.email,
      label: text[2],
      type: "email",
    },
    {
      Component: MaskedTextField,
      required: true,
      helperText: t.common.requiredField,
      name: "ddd",
      id: "outlined-ddd-input",
      value: currentFields.ddd,
      label: text[3],
      type: "tel",
      mask: "(##)"
    },
    {
      Component: MaskedTextField,
      required: true,
      helperText: t.common.requiredField,
      name: "telephone",
      id: "outlined-tel-input",
      value: currentFields.telephone,
      label: text[4],
      type: "tel",
      mask: "#####-####"
    }
  ]

  const telRef = useRef<HTMLInputElement>(null)

  const renderInputs = ({
    Component,
    required,
    helperText,
    name,
    id,
    value,
    label,
    type,
    mask
  }: fieldProps, index: number, mob: boolean) => {
    // Erro vindo do 422 da API tem precedência sobre o "campo obrigatório"
    // local: é mais específico e explica o motivo real da recusa.
    const erroDoCampo = fieldErrors?.[name]?.[0]

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentFields(event)

      if (name === 'ddd') {
        const val = event.target.value.replace(/\D/g, '')
        
        if (val.length === 2 && telRef.current) {
          telRef.current.focus()
        }
      }
    }

    return <Component
      key={index}
      required={required}
      error={Boolean(erroDoCampo) || (showError && fieldsUnfilled.current.includes(name) && required)}
      helperText={erroDoCampo || (showError && fieldsUnfilled.current.includes(name) && helperText)}
      name={name}
      id={id}
      value={value}
      onChange={handleChange}
      fullWidth
      label={label}
      type={type}
      mask={mask}
      size={mob ? 'medium' : 'small'}
      inputRef={name === 'telephone' ? telRef : undefined}
    />
  }

  useEffect(() => {
    fieldsUnfilled.current = fields
      .filter(field => field.required)
      .reduce(
        (agg: string[], { name, value }) => {
          if (["", "null", null, undefined].includes(value)) agg.push(name)
          return agg
        }, []
      )

    setFormState('form2', (fieldsUnfilled.current.length === 0))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFields.email, currentFields.telephone])

  return (
    <Grid container item className={name ? `${name} contact-info form` : "contact-info form"} xs={12} sx={{ display: 'flex', gap: '20px' }}>
      <Grid item className="textArea">
        <ChoosePlanText name="contact-info-text" variables={{ text }} />
      </Grid>
      <Hidden mdUp>
        <Grid item className="formArea" xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {renderInputs(fields[0], 0, true)}
          <Grid item className="fractArea" xs={12} sx={{ display: 'flex', gap: '10px' }}>
            <Grid item xs={2}>{renderInputs(fields[1], 1, true)}</Grid>
            <Grid item xs={10}>{renderInputs(fields[2], 2, true)}</Grid>
          </Grid>
        </Grid>
      </Hidden>
      <Hidden only={['xs', 'xl']}>
        <Grid item className="formArea" xs={12}>
          {renderInputs(fields[0], 0, false)}
          <Grid item className="fractArea">
            <Grid item xs={2}>{renderInputs(fields[1], 1, false)}</Grid>
            <Grid item xs={10}>{renderInputs(fields[2], 2, false)}</Grid>
          </Grid>
        </Grid>
      </Hidden>
      <Hidden xlDown>
        <Grid item className="formArea" xs={12}>
          {renderInputs(fields[0], 0, true)}
          <Grid item className="fractArea">
            <Grid item xs={1.25}>{renderInputs(fields[1], 1, true)}</Grid>
            <Grid item xs={10.75}>{renderInputs(fields[2], 2, true)}</Grid>
          </Grid>
        </Grid>
      </Hidden>
    </Grid >
  )
}